/** A run of highlighted text: `mark` = a match. */
export interface HighlightPart {
  text: string;
  mark: boolean;
}

const NAMED: Readonly<Record<string, string>> = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'" };

/** Decodes the entities `WebUtility.HtmlEncode` produces (named and numeric). */
export function decodeEntities(value: string): string {
  return value.replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/giu, (entity, body: string) => {
    if (body.startsWith('#x') || body.startsWith('#X')) {
      const code = Number.parseInt(body.slice(2), 16);
      return Number.isFinite(code) && code <= 0x10ffff ? String.fromCodePoint(code) : entity;
    }
    if (body.startsWith('#')) {
      const code = Number.parseInt(body.slice(1), 10);
      return Number.isFinite(code) && code <= 0x10ffff ? String.fromCodePoint(code) : entity;
    }
    return NAMED[body.toLowerCase()] ?? entity;
  });
}

/**
 * Splits the server's highlighted text (HTML-encoded, matches in `<mark>…</mark>`) into plain text runs, so it can be
 * rendered as text nodes — nothing in it is ever interpreted as markup.
 */
export function parseHighlighted(html: string | null | undefined): HighlightPart[] {
  if (!html) return [];
  const parts: HighlightPart[] = [];
  const pattern = /<mark>([\s\S]*?)<\/mark>/gu;
  let last = 0;
  for (const match of html.matchAll(pattern)) {
    if (match.index > last) parts.push({ text: decodeEntities(html.slice(last, match.index)), mark: false });
    parts.push({ text: decodeEntities(match[1] ?? ''), mark: true });
    last = match.index + match[0].length;
  }
  if (last < html.length) parts.push({ text: decodeEntities(html.slice(last)), mark: false });
  return parts.filter((p) => p.text.length > 0);
}

/** The text without highlighting (for labels, tooltips, the palette). */
export function plainText(html: string | null | undefined): string {
  return parseHighlighted(html)
    .map((p) => p.text)
    .join('');
}
